import {computed, onUnmounted, reactive, ref, toRefs, type ComputedRef, type Ref} from "vue";

// ============================================================================
// Type Definitions
// ============================================================================

interface UseAudioRecorderOptions {
    // Feature toggles
    enablePauseResume?: boolean; // Default: true
    enableProgressTracking?: boolean; // Default: false

    // Limits (for progress tracking)
    sizeLimit?: number | null; // Bytes (e.g., 500 * 1048576 for 500MB)
    timeLimit?: number | null; // Seconds

    // File configuration
    fileNamingStrategy?: "timestamp" | "random" | ((index: number) => string);
    mimeType?: string; // Default: "audio/mp3; codecs=opus"
    fileExtension?: string; // Default: "mp3"

    // Callbacks
    onRecordingStart?: () => void;
    onRecordingStop?: () => void;
    onRecordingPause?: () => void;
    onRecordingResume?: () => void;
    onRecordingError?: (error: Error) => void;
    onPlaybackStart?: () => void;
    onPlaybackEnd?: () => void;

    // MediaRecorder config
    audioConstraints?: MediaStreamConstraints["audio"];
}

interface AudioRecorderState {
    recording: boolean;
    paused: boolean;
    finished: boolean;
    playing: boolean;
    recordingProgress: number;
    playbackProgress: number;
    recordedAudioTotalSize: number;
}

interface UseAudioRecorderReturn {
    // State (all refs for proper reactivity)
    recording: Ref<boolean>;
    paused: Ref<boolean>;
    finished: Ref<boolean>;
    playing: Ref<boolean>;
    recordingProgress: Ref<number>;
    playbackProgress: Ref<number>;
    recordedAudioTotalSize: Ref<number>;
    audioFile: Ref<Blob | null>;
    audioUrl: Ref<string | null>;
    isIdle: ComputedRef<boolean>;
    isRecorded: ComputedRef<boolean>;
    recordingState: ComputedRef<"idle" | "recording" | "recorded">;

    // Methods
    startRecording: () => Promise<void>;
    stopRecording: () => void;
    pauseRecording: () => void;
    resumeRecording: () => void;
    playRecording: () => void;
    pausePlayback: () => void;
    togglePlayback: () => void;
    deleteRecording: () => void;
    getRecordingFile: () => File | null;
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_OPTIONS: Required<
    Omit<
        UseAudioRecorderOptions,
        | "onRecordingStart"
        | "onRecordingStop"
        | "onRecordingPause"
        | "onRecordingResume"
        | "onRecordingError"
        | "onPlaybackStart"
        | "onPlaybackEnd"
    >
> = {
    enablePauseResume: true,
    enableProgressTracking: false,
    sizeLimit: null,
    timeLimit: null,
    fileNamingStrategy: "timestamp",
    mimeType: "audio/mp3; codecs=opus",
    fileExtension: "mp3",
    audioConstraints: {
        channelCount: 1,
        noiseSuppression: true,
        echoCancellation: false,
    },
};

// ============================================================================
// Composable
// ============================================================================

export default (userOptions: UseAudioRecorderOptions = {}): UseAudioRecorderReturn => {
    // Merge options with defaults
    const options = {...DEFAULT_OPTIONS, ...userOptions};

    // ========================================================================
    // Internal State
    // ========================================================================

    const mediaRecorder = ref<MediaRecorder | null>(null);
    const audioChunks = ref<Blob[]>([]);
    const audioElement = ref<HTMLAudioElement | null>(null);
    const mediaStream = ref<MediaStream | null>(null);

    // ========================================================================
    // Exposed State
    // ========================================================================

    const state = reactive<AudioRecorderState>({
        recording: false,
        playing: false,
        paused: false,
        finished: false,
        recordingProgress: 0,
        playbackProgress: 0,
        recordedAudioTotalSize: 0,
    });

    const audioFile = ref<Blob | null>(null);
    const audioUrl = ref<string | null>(null);

    // Computed properties
    const isIdle = computed(() => !state.recording && !state.finished);
    const isRecorded = computed(() => state.finished && audioFile.value !== null);
    const recordingState = computed<"idle" | "recording" | "recorded">(() => {
        if (state.recording) {
            return "recording";
        }
        if (state.finished) {
            return "recorded";
        }
        return "idle";
    });

    // ========================================================================
    // File Naming Utilities
    // ========================================================================

    const generateFileName = () => {
        const strategy = options.fileNamingStrategy;
        const ext = options.fileExtension;

        if (typeof strategy === "function") {
            return strategy(audioChunks.value.length);
        }

        switch (strategy) {
            case "timestamp":
                return `audio-${Date.now()}.${ext}`;
            case "random":
                const hash1 = Math.random().toString(36).slice(2, 20);
                const hash2 = Math.random().toString(36).slice(2, 20);
                return `${hash1}${hash2}.${ext}`;
            default:
                return `audio-${Date.now()}.${ext}`;
        }
    };

    // ========================================================================
    // Progress Calculation
    // ========================================================================

    const calculateRecordingProgress = (event: BlobEvent) => {
        if (options.timeLimit) {
            // Time-based progress (prioritized over size)
            state.recordedAudioTotalSize = 0;
            // Calculate based on chunks count and timeslice (100ms)
            const elapsedSeconds = (audioChunks.value.length * 100) / 1000;
            return Math.round((elapsedSeconds / options.timeLimit) * 100);
        } else if (options.sizeLimit) {
            // Size-based progress
            state.recordedAudioTotalSize += event.data.size;
            return Math.round((state.recordedAudioTotalSize / options.sizeLimit) * 100);
        } else {
            // No limit: default to 500MB max
            state.recordedAudioTotalSize += event.data.size;
            const defaultLimit = 500 * 1048576; // 500MB
            return Math.round((state.recordedAudioTotalSize / defaultLimit) * 100);
        }
    };

    // ========================================================================
    // Recording Event Handlers
    // ========================================================================

    const handleDataAvailable = (event: BlobEvent) => {
        if (!options.enableProgressTracking) {
            // Simple mode: just collect chunks
            audioChunks.value.push(event.data);
            return;
        }

        // Progress tracking mode
        const shouldContinue = state.recordingProgress < 100 || (!options.timeLimit && !options.sizeLimit);

        if (shouldContinue) {
            audioChunks.value.push(event.data);
            state.recordingProgress = calculateRecordingProgress(event);
        } else {
            // Limit reached, auto-stop
            stopRecording();
        }
    };

    const handleRecordingStop = () => {
        // Stop all media tracks
        mediaStream.value?.getTracks().forEach((track) => track.stop());

        // Create blob and file
        const blob = new Blob(audioChunks.value, {type: options.mimeType});
        const fileName = generateFileName();
        audioFile.value = new File([blob], fileName, {
            type: options.mimeType,
            lastModified: Date.now(),
        });

        // Create object URL for playback
        audioUrl.value = URL.createObjectURL(blob);

        // Update state
        state.recording = false;
        state.paused = false;
        state.finished = true;

        // Cleanup recorder
        mediaRecorder.value = null;
        mediaStream.value = null;

        // Call callback
        options.onRecordingStop?.();
    };

    // ========================================================================
    // Playback Event Handlers
    // ========================================================================

    const handlePlaybackEnd = () => {
        state.playing = false;
        state.playbackProgress = 0;
        options.onPlaybackEnd?.();
    };

    const handlePlaybackProgress = () => {
        if (!audioElement.value) {
            return;
        }

        const {currentTime, duration} = audioElement.value;
        state.playbackProgress = Math.round((currentTime / duration) * 100);
    };

    // ========================================================================
    // Recording Lifecycle Methods
    // ========================================================================

    const startRecording = async () => {
        try {
            // Check if already recording
            if (state.recording) {
                console.warn("Recording already in progress");
                return;
            }

            // Check browser support
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("getUserMedia is not supported in this browser");
            }

            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: options.audioConstraints,
            });

            deleteRecording();

            // Store stream reference for cleanup
            mediaStream.value = stream;

            // Create MediaRecorder
            mediaRecorder.value = new MediaRecorder(stream);
            audioChunks.value = [];

            // Setup event handlers
            mediaRecorder.value.ondataavailable = handleDataAvailable;
            mediaRecorder.value.onstop = handleRecordingStop;

            // Start recording
            // Pass timeslice (100ms) if progress tracking is enabled
            mediaRecorder.value.start(options.enableProgressTracking ? 100 : undefined);

            // Update state
            state.recording = true;
            state.finished = false;
            state.recordingProgress = 0;
            state.recordedAudioTotalSize = 0;

            // Call callback
            options.onRecordingStart?.();
        } catch (error) {
            console.error("Failed to start recording:", error);
            options.onRecordingError?.(error as Error);
            throw error;
        }
    };

    const stopRecording = () => {
        if (!mediaRecorder.value || !state.recording) {
            return;
        }

        mediaRecorder.value.stop();
        // Note: cleanup happens in onstop handler
    };

    const pauseRecording = () => {
        if (!options.enablePauseResume) {
            console.warn("Pause/Resume is not enabled for this recorder");
            return;
        }

        if (!mediaRecorder.value || state.paused) {
            return;
        }

        mediaRecorder.value.pause();
        state.paused = true;
        options.onRecordingPause?.();
    };

    const resumeRecording = () => {
        if (!options.enablePauseResume) {
            console.warn("Pause/Resume is not enabled for this recorder");
            return;
        }

        if (!mediaRecorder.value || !state.paused) {
            return;
        }

        mediaRecorder.value.resume();
        state.paused = false;
        options.onRecordingResume?.();
    };

    // ========================================================================
    // Playback Methods
    // ========================================================================

    const playRecording = () => {
        if (!audioUrl.value) {
            console.warn("No recording available to play");
            return;
        }

        // Create audio element on first play
        if (!audioElement.value) {
            audioElement.value = new Audio(audioUrl.value);

            // Setup event listeners
            audioElement.value.addEventListener("ended", handlePlaybackEnd);
            audioElement.value.addEventListener("timeupdate", handlePlaybackProgress);
        }

        audioElement.value.play();
        state.playing = true;
        options.onPlaybackStart?.();
    };

    const pausePlayback = () => {
        if (!audioElement.value || !state.playing) {
            return;
        }

        audioElement.value.pause();
        state.playing = false;
    };

    const togglePlayback = () => {
        if (state.playing) {
            pausePlayback();
        } else {
            playRecording();
        }
    };

    // ========================================================================
    // Cleanup Methods
    // ========================================================================

    const deleteRecording = () => {
        // Stop and cleanup audio element
        if (audioElement.value) {
            audioElement.value.pause();
            audioElement.value.removeEventListener("ended", handlePlaybackEnd);
            audioElement.value.removeEventListener("timeupdate", handlePlaybackProgress);
            audioElement.value = null;
        }

        // Revoke object URL (prevents memory leak)
        if (audioUrl.value) {
            URL.revokeObjectURL(audioUrl.value);
            audioUrl.value = null;
        }

        // Stop any ongoing recording
        if (mediaRecorder.value && state.recording) {
            mediaRecorder.value.stop();
        }

        // Stop media stream
        mediaStream.value?.getTracks().forEach((track) => track.stop());
        mediaStream.value = null;

        // Reset state
        audioFile.value = null;
        audioChunks.value = [];
        state.recording = false;
        state.playing = false;
        state.paused = false;
        state.finished = false;
        state.recordingProgress = 0;
        state.playbackProgress = 0;
        state.recordedAudioTotalSize = 0;

        // Cleanup recorder reference
        mediaRecorder.value = null;
    };

    const getRecordingFile = () => {
        return audioFile.value;
    };

    // ========================================================================
    // Lifecycle Hooks
    // ========================================================================

    // Automatic cleanup on component unmount
    onUnmounted(() => {
        deleteRecording();
    });

    // ========================================================================
    // Return API
    // ========================================================================

    return {
        // State (convert reactive state to refs for proper reactivity)
        ...toRefs(state),
        audioFile,
        audioUrl,
        isIdle,
        isRecorded,
        recordingState,

        // Methods
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        playRecording,
        pausePlayback,
        togglePlayback,
        deleteRecording,
        getRecordingFile,
    };
};
